import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseCommonParamsOptions {
  defaultSortKey?: string;
  defaultSortAscending?: boolean;
  sortKeyMap?: Record<string, string>;
  defaultView?: string;
}

interface SortType {
  isAscending: boolean;
  key: string;
}

interface CommonParams {
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  page: number;
  sortType: SortType;
  resolvedSortKey: string;
  getSortParam: () => string;
  searchText: string;
  setPage: (newPage: number) => void;
  setSortType: (newSortType: SortType) => void;
  setSortDirection: (isAscending: boolean) => void;
  setSearchText: (newSearch: string) => void;
  getFilter: (key: string, defaultValue?: string) => string;
  setFilter: (key: string, value: string | undefined | null) => void;
  clearFilter: (key: string) => void;
  clearAllFilters: (keys: string[]) => void;
  hasAnyFilter: (keys: string[]) => boolean;
  view: string;
  setView: (view: string) => void;
  tab: string | null;
  setTab: (tab: string | null) => void;
}

export const useCommonParams = (options: UseCommonParamsOptions = {}): CommonParams => {
  const { defaultSortKey = 'dateCreated', defaultSortAscending = false, sortKeyMap = {}, defaultView } = options;
  const [searchParams, setSearchParamsRaw] = useSearchParams();

  const setSearchParams: typeof setSearchParamsRaw = useCallback((nextInit, navigateOpts) => {
    const result =
      typeof nextInit === 'function'
        ? nextInit(new URLSearchParams(window.location.search))
        : nextInit;
    const resultStr = (result instanceof URLSearchParams ? result : new URLSearchParams(result as any)).toString();
    const currentStr = new URLSearchParams(window.location.search).toString();
    if (resultStr === currentStr) return;
    setSearchParamsRaw(result, navigateOpts);
  }, [setSearchParamsRaw]);

  const page = useMemo(() => {
    const urlPage = searchParams.get('page');
    const parsed = urlPage ? parseInt(urlPage, 10) : 1;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  }, [searchParams]);

  const sortType = useMemo(() => {
    const urlSort = searchParams.get('sort');
    if (urlSort) {
      const isAscending = !urlSort.startsWith('-');
      const key = isAscending ? urlSort : urlSort.slice(1);
      return { isAscending, key };
    }
    return {
      isAscending: defaultSortAscending,
      key: defaultSortKey,
    };
  }, [searchParams, defaultSortKey, defaultSortAscending]);

  const resolvedSortKey = useMemo(() => {
    return sortKeyMap[sortType.key] || sortType.key;
  }, [sortType.key, sortKeyMap]);

  const getSortParam = useCallback(() => {
    const key = resolvedSortKey;
    return `${sortType.isAscending ? '' : '-'}${key}`;
  }, [resolvedSortKey, sortType.isAscending]);

  const searchText = useMemo(() => {
    return searchParams.get('search') || '';
  }, [searchParams]);

  const setPage = (newPage: number) => {
    setSearchParams((p) => {
      if (newPage === 1) {
        p.delete('page');
      } else {
        p.set('page', newPage.toString());
      }
      return p;
    });
  };

  const setSortType = (newSortType: SortType) => {
    const { isAscending, key } = newSortType;
    const sortValue = `${isAscending ? '' : '-'}${key}`;
    setSearchParams((p) => {
      if (sortValue === `${defaultSortAscending ? '' : '-'}${defaultSortKey}`) {
        p.delete('sort');
      } else {
        p.set('sort', sortValue);
      }
      return p;
    });
  };

  const setSortDirection = useCallback((isAscending: boolean) => {
    setSortType({ ...sortType, isAscending });
  }, [sortType, setSortType]);

  const setSearchText = (newSearch: string) => {
    setSearchParams((p) => {
      if (!newSearch) {
        p.delete('search');
      } else {
        p.set('search', newSearch);
      }
      return p;
    });
  };

  const getFilter = useCallback((key: string, defaultValue: string = ''): string => {
    return searchParams.get(key) || defaultValue;
  }, [searchParams]);

  const setFilter = useCallback((key: string, value: string | undefined | null) => {
    setSearchParams((p) => {
      if (!value) {
        p.delete(key);
      } else {
        p.set(key, value);
      }
      return p;
    });
  }, [setSearchParams]);

  const clearFilter = useCallback((key: string) => {
    setSearchParams((p) => {
      p.delete(key);
      return p;
    });
  }, [setSearchParams]);

  const clearAllFilters = useCallback((keys: string[]) => {
    setSearchParams((p) => {
      keys.forEach((key) => p.delete(key));
      return p;
    });
  }, [setSearchParams]);

  const hasAnyFilter = useCallback((keys: string[]): boolean => {
    return keys.some((key) => {
      const value = searchParams.get(key);
      return value !== null && value !== '';
    });
  }, [searchParams]);

  const view = useMemo(() => {
    return searchParams.get('view') || defaultView || '';
  }, [searchParams, defaultView]);

  const setView = useCallback((newView: string) => {
    setSearchParams((p) => {
      if (!newView || newView === defaultView) {
        p.delete('view');
      } else {
        p.set('view', newView);
      }
      return p;
    });
  }, [setSearchParams, defaultView]);

  const tab = useMemo(() => {
    return searchParams.get('tab');
  }, [searchParams]);

  const setTab = useCallback((newTab: string | null) => {
    setSearchParams((p) => {
      if (!newTab) {
        p.delete('tab');
      } else {
        p.set('tab', newTab);
      }
      return p;
    });
  }, [setSearchParams]);

  return {
    searchParams,
    setSearchParams,
    page,
    sortType,
    resolvedSortKey,
    getSortParam,
    searchText,
    setPage,
    setSortType,
    setSortDirection,
    setSearchText,
    getFilter,
    setFilter,
    clearFilter,
    clearAllFilters,
    hasAnyFilter,
    view,
    setView,
    tab,
    setTab,
  };
};